import { Atom, Getter, PrimitiveAtom, SetStateAction, atom } from "jotai";
import { focusAtom } from "jotai-optics";
import { sbaLoanAmortizationScheduleAtom } from "./amortization";
import { createStorableCellAtom } from "./cell";
import { createItemizationAtom } from "./itemization";
import { modelAtom } from "./model";
import { createOverridableCellAtom } from "./overridable";
import { growthRateModeAtom } from "./projections";
import { CellTypes, DollarCell, GrowthCell, PercentCell } from "./types";

export const iterationCountAtom = focusAtom(modelAtom, (optic) =>
  optic.prop("general").prop("iterationCount").valueOr(0),
);

export const suggestedIterationCountAtom = atom((get) => {
  return Math.ceil(get(sbaLoanAmortizationScheduleAtom));
});

export const createIterationMolecule = (
  initialAtom: PrimitiveAtom<CellTypes>,
  growthCellAtom: PrimitiveAtom<CellTypes>,
  fn: (
    get: Getter,
    index: number,
    prevAtoms: PrimitiveAtom<CellTypes>[],
  ) => PrimitiveAtom<CellTypes>,
) => {
  return atom((get) => {
    const iterationCount = get(iterationCountAtom) || 0;
    const prevAtoms: PrimitiveAtom<CellTypes>[] = [];
    return [
      initialAtom,
      growthCellAtom,
      ...Array.from({ length: iterationCount }, (_, i) => {
        prevAtoms.push(fn(get, i, prevAtoms));
        return prevAtoms[i];
      }),
    ];
  });
};
export const createGrowthRateCellAtom = ({
  id,
  value = 0.05,
}: {
  id: string;
  value?: number;
}) => {
  return createStorableCellAtom(`projection|growthRate|${id}`, {
    type: "growth",
    value,
  }) as PrimitiveAtom<GrowthCell>;
};

export const createGrowthRateCompoundAnnualIterationMolecule = (
  id: string,
  initialAtom: PrimitiveAtom<CellTypes>,
  growthCellAtom: PrimitiveAtom<GrowthCell> | PrimitiveAtom<PercentCell>,
  options?: { isNegative: boolean },
  yoyPercentCellsMolecule?: Atom<PrimitiveAtom<CellTypes>[]>,
) => {
  const cache: Record<number, PrimitiveAtom<CellTypes>> = {};

  return atom((get) => {
    const iterationCount = get(iterationCountAtom) || 0;
    const yoyPercentAtoms = !yoyPercentCellsMolecule
      ? null
      : get(yoyPercentCellsMolecule);

    return [
      initialAtom,
      growthCellAtom as PrimitiveAtom<CellTypes>,
      ...Array.from({ length: iterationCount }, (_, index) => {
        if (cache[index]) return cache[index];

        const newCellAtom = atom((get) => {
          const prevAtom = index === 0 ? initialAtom : cache[index - 1];
          const prevValue = Number(get(prevAtom).value);

          let percent = Number(
            get(growthCellAtom as PrimitiveAtom<PercentCell>).value,
          );

          if (yoyPercentAtoms) {
            const yoyPercent = Number(
              get(
                yoyPercentAtoms[
                  Math.max(0, index + 2)
                ] as PrimitiveAtom<PercentCell>,
              ).value,
            );

            percent = isNaN(yoyPercent) ? 0 : yoyPercent;
          }

          const newValue = Math.round(prevValue * (1 + percent) * 100) / 100;

          return {
            ...get(prevAtom),
            value: newValue,
            isNegative: options?.isNegative || false,
          } as CellTypes;
        });

        const o = createOverridableCellAtom(
          `projection|iteration|${id}|${index}`,
          newCellAtom,
        );

        cache[index] = o;

        return o;
      }),
    ];
  });
};

export const createGrowthRatePerYearIterationMolecule = (
  id: string,
  initialAtom: PrimitiveAtom<CellTypes>,
  growthCellAtom: PrimitiveAtom<GrowthCell>,
) => {
  const cache: Record<number, PrimitiveAtom<CellTypes>> = {};

  return atom((get) => {
    const iterationCount = get(iterationCountAtom) || 0;

    return [
      initialAtom,
      growthCellAtom as PrimitiveAtom<CellTypes>,
      ...Array.from({ length: iterationCount }, (_, index) => {
        if (cache[index]) return cache[index];

        const o = createStorableCellAtom(
          `projection|iteration|perYear|${id}|${index}`,
          {
            value: 0.05,
            type: "percent",
          },
        );

        cache[index] = o;

        return o;
      }),
    ];
  });
};

export const createGrowthRateContainer = (
  id: string,
  options?: { isNegative: boolean },
) => {
  const modeAtom = growthRateModeAtom;

  const intialDollarCell = createStorableCellAtom(
    `itemizationIterationFirstYearCell|${id}|consolidated`,
    {
      type: "dollar",
      value: 0,
      isNegative: options?.isNegative || false,
    },
  );

  const growthRateCellAtom = createGrowthRateCellAtom({ id });

  const compoundAnnualMolecule =
    createGrowthRateCompoundAnnualIterationMolecule(
      `itemizationIteration|${id}|consolidated`,
      intialDollarCell,
      growthRateCellAtom,
      { isNegative: options?.isNegative || false },
    );

  const compoundAnnualYoYMolecule = createComputedIterationMolecule((i) => {
    return atom((get) => {
      if (i === 0) {
        return blankGrowthCellPlaceholder as unknown as CellTypes;
      }

      const currentYearValue = Number(
        get(get(compoundAnnualMolecule)[i]).value || 0,
      );
      const prevIndex = Math.max(0, i === 2 ? 0 : i - 1);
      const previousYearValue =
        i < 2
          ? 0
          : Number(get(get(compoundAnnualMolecule)[prevIndex]).value || 0);

      return {
        type: "percent",
        isLocked: true,
        value: i < 2 ? null : currentYearValue / previousYearValue - 1,
      };
    });
  });

  const perYearMolecule = createGrowthRatePerYearIterationMolecule(
    `itemizationIteration|${id}|perYear`,
    atom(blankGrowthCellPlaceholder as unknown as CellTypes),
    atom(blankGrowthCellPlaceholder as unknown as GrowthCell),
  );

  const perYearYoYMolecule = createComputedIterationMolecule(
    (i, _index, prevAtoms) => {
      if (i === 0) {
        return intialDollarCell;
      }

      return atom((get) => {
        const prevDollarValue =
          i <= 2
            ? Number(get(intialDollarCell).value)
            : Number(get(prevAtoms[i - 1]).value);

        const prevYearPercent = Number(get(get(perYearMolecule)[i]).value || 0);

        return {
          type: "dollar",
          isLocked: true,
          value: i < 2 ? null : prevDollarValue * (1 + prevYearPercent),
          isNegative: options?.isNegative || false,
        };
      });
    },
    atom(
      (get) => {
        const perYearCellAtoms = get(perYearMolecule);
        const vals = perYearCellAtoms
          .map((cell, i) => (i > 1 ? Number(get(cell).value || 0) : null))
          .filter((v) => v !== null);

        const total = vals.reduce(
          (acc, value) => Number(acc) + Number(value),
          0,
        );
        const avg = Number(total) / Number(vals.length);

        return {
          type: "growth",
          value: avg,
          isLocked: true,
          tooltip: !isNaN(avg)
            ? "Average growth rate for all year-over-year percentages."
            : undefined,
        };
      },
      (_get, set, update: SetStateAction<GrowthCell>) => {
        set(growthRateCellAtom, update);
      },
    ) as PrimitiveAtom<CellTypes>,
  );

  return {
    modeAtom,
    compoundAnnualMolecule,
    compoundAnnualYoYMolecule,
    perYearMolecule,
    perYearYoYMolecule,
    growthRateCellAtom,
    intialDollarCell,
  };
};

export const createAggregatedIterationAtom = (
  id: string,
  options?: { isNegative: boolean },
) => {
  const gr = createGrowthRateContainer(id, options);

  return createItemizationAtom({
    id,
    growthRateContainer: gr,
    lineItemToMolecule: (lineItem) =>
      atom((get) => {
        const growthRateMode = get(growthRateModeAtom);
        const firstYearCell = createStorableCellAtom(
          `itemizationIterationFirstYearCell|${id}|lineItem|${lineItem.id}`,
          {
            type: "dollar",
            value: 0,
            isNegative: options?.isNegative || false,
          },
        );

        const compoundAnnualMolecule =
          createGrowthRateCompoundAnnualIterationMolecule(
            `itemizationIteration|${id}|lineItem|${lineItem.id}`,
            firstYearCell,

            growthRateMode === "compoundAnnual"
              ? (createStorableCellAtom(
                  `projection|growthRate|${`itemizationIteration|${id}|lineItem|${lineItem.id}`}`,
                  {
                    type: "growth",
                    value: 0.05,
                  },
                ) as PrimitiveAtom<GrowthCell>)
              : atom({
                  value: null,
                  isLocked: true,
                  type: "growth",
                } as GrowthCell),

            { isNegative: options?.isNegative || false },
            growthRateMode === "compoundAnnual"
              ? undefined
              : gr.perYearMolecule,
          );

        return get(compoundAnnualMolecule);
      }),

    lineItemColumnToCellAtom: (cells, index) => {
      //if (index === 0) return gr.intialDollarCell;

      if (index === 1)
        return atom(
          (get) => {
            if (get(growthRateModeAtom) !== "perYear") {
              return {
                type: "growth",
                value: "" as unknown as number,
                isLocked: true,
              } as GrowthCell;
            }

            const perYearCellAtoms = get(gr.perYearMolecule);
            const growthRates = perYearCellAtoms
              .map((cell, i) => (i > 1 ? Number(get(cell).value || 0) : null))
              .filter((v) => v !== null);

            // Multiply growth factors: (1 + r)
            const totalGrowthFactor = growthRates.reduce((acc, rate) => {
              return Number(acc) * (1 + Number(rate));
            }, 1);

            const numberOfYears = growthRates.length;

            // Compound Annual Growth Rate (CAGR)
            const avg =
              Math.pow(Number(totalGrowthFactor), 1 / numberOfYears) - 1;

            return {
              type: "growth",
              value: avg,
              isLocked: true,
              tooltip:
                "Average growth rate for all year-over-year percentages.",
            };
          },
          (_get, set, update: SetStateAction<GrowthCell>) => {
            set(gr.growthRateCellAtom, update);
          },
        );

      return atom((get) => {
        const values = cells.map((cell) => Number(get(cell).value || 0));
        const sum = values.reduce((acc, value) => acc + value, 0);

        return {
          type: "dollar",
          value: sum || 0,
          isNegative: options?.isNegative || false,
          isLocked: true,
        } as DollarCell;
      });
    },
  });
};

const blankGrowthCellPlaceholder = atom({
  type: "blank",
} as CellTypes);

export const createComputedIterationMolecule = (
  fn: (
    index: number,
    iterationIndex: number,
    prev: Atom<CellTypes>[],
  ) => Atom<CellTypes>,
  effectAtom?: PrimitiveAtom<CellTypes>,
  options?: {
    extraColumnCount?: number;
  },
) => {
  const prevAtoms: Atom<CellTypes>[] = [];
  const cache: Record<number, Atom<CellTypes>> = {};
  const extraColumnCount =
    options?.extraColumnCount === undefined ? 2 : options.extraColumnCount;

  return atom((get) => {
    const iterationCount = get(iterationCountAtom) || 0;

    return Array.from({ length: iterationCount + extraColumnCount }, (_, i) => {
      if (cache[i]) return cache[i];

      const iterationIndex = i === 0 || i === 1 ? i : i - 1;
      const newAtom =
        i !== 1
          ? fn(i, iterationIndex, prevAtoms)
          : effectAtom || blankGrowthCellPlaceholder;

      prevAtoms.push(newAtom);
      cache[i] = newAtom;
      return newAtom;
    });
  });
};

export const createComputedIterationRawMolecule = (
  fn: (index: number, prev: Atom<number>[]) => Atom<number>,
) => {
  const prevAtoms: Atom<number>[] = [];
  const cache: Record<number, Atom<number>> = {};
  return atom((get) => {
    const iterationCount = get(iterationCountAtom) || 0;

    return Array.from({ length: iterationCount }, (_, i) => {
      if (cache[i]) return cache[i];

      const newAtom = fn(i, prevAtoms);
      prevAtoms.push(newAtom);
      cache[i] = newAtom;
      return newAtom;
    });
  });
};

export const createStorableDollarIterationMolecule = (
  id: string,
  initialCellProps: Partial<{ isLocked: boolean; isNegative: boolean }> = {},
  laterCellProps: Partial<{ isLocked: boolean; isNegative: boolean }> = {},
) => {
  return createIterationMolecule(
    createStorableCellAtom(`${id}|startiteration|0`, {
      type: "dollar",
      value: null,
      ...initialCellProps,
    }),
    blankGrowthCellPlaceholder,
    (_get, index) =>
      createStorableCellAtom(`${id}|iteration|${index}`, {
        type: "dollar",
        value: null,
        ...laterCellProps,
      }),
  );
};
